import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Cloud, Mail, Lock, AlertCircle, CheckCircle2, ArrowLeft, Globe2, Moon, Sun } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useI18n } from '../i18n';
import { useTenantOptional } from '../contexts/TenantContext';
import { TenantId } from '../config/tenant';
import { supabase } from '../config/supabase';
import '../styles/auth.css';

const ResetPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isRecoveryMode, setIsRecoveryMode] = useState(false);

    const { resetPassword, updatePassword } = useAuthStore();
    const navigate = useNavigate();
    const { tenant: tenantParam } = useParams<{ tenant: string }>();
    const tenantContext = useTenantOptional();
    const { t, lang, switchTo } = useI18n();

    const currentTenant: TenantId = (tenantParam === 'cn' || tenantParam === 'intl')
        ? tenantParam
        : (tenantContext?.tenant || 'cn');

    // 监听 PASSWORD_RECOVERY 事件以切换到设置新密码模式
    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'PASSWORD_RECOVERY') {
                setIsRecoveryMode(true);
                setSuccess('');
                setError('');
            }
        });

        // 检查 URL 中是否已有 recovery token（页面刷新场景）
        const hash = window.location.hash;
        if (hash.includes('type=recovery')) {
            setIsRecoveryMode(true);
        }

        return () => subscription.unsubscribe();
    }, []);

    // 发送重置邮件
    const handleSendReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        try {
            await resetPassword(email);
            setSuccess(t('resetEmailSent'));
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    // 设置新密码
    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword.length < 6) {
            setError(t('passwordMinLength'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('passwordsDoNotMatch'));
            return;
        }

        setIsLoading(true);
        try {
            await updatePassword(newPassword);
            setSuccess(t('passwordResetSuccess'));
            setTimeout(() => navigate(`/${currentTenant}/login`), 2000);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`auth-background ${!isDarkMode ? 'light-mode' : ''}`}>
            <div className="auth-orb auth-orb-1" />
            <div className="auth-orb auth-orb-2" />
            <div className="auth-orb auth-orb-3" />

            <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
                <div className="auth-card auth-card-enter w-full max-w-[400px] p-10">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-4">
                            <div className="auth-icon-container">
                                <Cloud strokeWidth={1.5} />
                            </div>
                        </div>
                        <h1 className="auth-title">
                            {isRecoveryMode ? t('setNewPassword') : t('resetPasswordTitle')}
                        </h1>
                        <p className="auth-subtitle">
                            {isRecoveryMode ? t('setNewPasswordDesc') : t('resetPasswordDesc')}
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="auth-error">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Success */}
                    {success && (
                        <div className="flex items-center gap-3 p-4 rounded-xl mb-5"
                            style={{
                                background: isDarkMode ? 'rgba(52, 199, 89, 0.15)' : 'rgba(52, 199, 89, 0.1)',
                                border: `1px solid ${isDarkMode ? 'rgba(52, 199, 89, 0.3)' : 'rgba(52, 199, 89, 0.25)'}`,
                                color: isDarkMode ? '#34c759' : '#2d9f4e'
                            }}
                        >
                            <CheckCircle2 size={18} />
                            <div>
                                <p className="text-sm font-medium">{success}</p>
                                {!isRecoveryMode && (
                                    <p className="text-xs mt-1 opacity-80">{t('resetEmailSentDesc')}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 模式一：请求重置 */}
                    {!isRecoveryMode && !success && (
                        <form onSubmit={handleSendReset} className="space-y-4">
                            <div className="auth-input-group">
                                <div className="relative">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="auth-input"
                                        placeholder={t('emailAddress')}
                                        autoComplete="email"
                                        required
                                    />
                                    <Mail size={18} className="auth-input-icon" />
                                </div>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isLoading || !email.trim()}
                                    className="auth-button-primary"
                                >
                                    <span className="flex items-center justify-center">
                                        {isLoading && <span className="auth-spinner" />}
                                        {isLoading ? t('sendingResetLink') : t('sendResetLink')}
                                    </span>
                                </button>
                            </div>
                        </form>
                    )}

                    {/* 模式二：设置新密码 */}
                    {isRecoveryMode && !success && (
                        <form onSubmit={handleUpdatePassword} className="space-y-4">
                            <div className="auth-input-group">
                                <div className="relative">
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="auth-input"
                                        placeholder={t('newPassword')}
                                        autoComplete="new-password"
                                        required
                                        minLength={6}
                                    />
                                    <Lock size={18} className="auth-input-icon" />
                                </div>
                            </div>

                            <div className="auth-input-group">
                                <div className="relative">
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="auth-input"
                                        placeholder={t('confirmPassword')}
                                        autoComplete="new-password"
                                        required
                                        minLength={6}
                                    />
                                    <Lock size={18} className="auth-input-icon" />
                                </div>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isLoading || !newPassword || !confirmPassword}
                                    className="auth-button-primary"
                                >
                                    <span className="flex items-center justify-center">
                                        {isLoading && <span className="auth-spinner" />}
                                        {isLoading ? t('updatingPassword') : t('updatePasswordAction')}
                                    </span>
                                </button>
                            </div>
                        </form>
                    )}

                    {/* 返回登录链接 */}
                    <div className="text-center mt-6">
                        <Link
                            to={`/${currentTenant}/login`}
                            className="auth-link inline-flex items-center gap-2 text-sm"
                        >
                            <ArrowLeft size={14} />
                            {t('backToLogin')}
                        </Link>
                    </div>

                    {/* Footer */}
                    <div className="auth-footer mt-8">
                        <button
                            type="button"
                            onClick={() => setIsDarkMode(!isDarkMode)}
                            className="auth-footer-button"
                        >
                            {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
                        </button>
                        <button
                            type="button"
                            onClick={() => switchTo(lang === 'zh' ? 'en' : 'zh')}
                            className="auth-footer-button"
                        >
                            <Globe2 size={14} />
                            {lang === 'zh' ? 'English' : '中文'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
